import { Button, Input } from '@syncanix/ui';
import { useCallback, useState } from 'react';

import { OutsideShellHeader } from '@/components/OutsideShellHeader';
import { env } from '@/config/env';
import { useT } from '@/core/providers/I18nProvider';

import { resendVerification, type ResendOutcome } from './resend-verification';

import type { FormEvent, ReactElement } from 'react';

interface VerifyEmailScreenProps {
  /** Returns to the Auth0 login (re-trigger after the user has verified). */
  readonly onBackToSignIn: () => void;
  /** Injectable for tests; defaults to the real resend client. */
  readonly resend?: typeof resendVerification;
}

type Status = 'idle' | 'sending' | ResendOutcome;

// Literal catalog keys per outcome (a lookup record so the i18n key-coverage
// scanner sees each key as a literal — never build these dynamically).
const OUTCOME_MESSAGE_KEY: Record<ResendOutcome, string> = {
  sent: 'verifyEmail.resend.sent',
  invalid: 'verifyEmail.resend.invalid',
  rate_limited: 'verifyEmail.resend.rateLimited',
  error: 'verifyEmail.resend.error',
};

/**
 * Shown when Auth0's "Require verified email" gate blocks a sign-in
 * (`access_denied`). Replaces the generic error card with a clear, localized
 * next-step screen: the link was emailed to the signup address, check spam, or
 * use Continue with Google — plus a resend form for a missed/expired link.
 *
 * The resend response is NON-ENUMERABLE: `sent` shows a generic "if an account
 * needs verification, we've sent a link" message regardless of whether the
 * address exists, so this screen can't be used to probe for accounts.
 */
export function VerifyEmailScreen({
  onBackToSignIn,
  resend,
}: VerifyEmailScreenProps): ReactElement {
  const { t } = useT();
  const send = resend ?? resendVerification;
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>): void => {
      e.preventDefault();
      const trimmed = email.trim();
      // Light client-side shape check (the server validates authoritatively);
      // a malformed address short-circuits to the same localized invalid copy.
      if (trimmed.length === 0 || !trimmed.includes('@')) {
        setStatus('invalid');
        return;
      }
      setStatus('sending');
      void send(trimmed, { endpoint: env.VITE_GRAPHQL_ENDPOINT }).then(setStatus);
    },
    [email, send],
  );

  const outcomeKey = status === 'idle' || status === 'sending' ? null : OUTCOME_MESSAGE_KEY[status];

  return (
    <div className="flex min-h-screen flex-col">
      <OutsideShellHeader />
      <main className="flex flex-1 items-center justify-center p-8">
        <section className="max-w-md space-y-6">
          <div className="space-y-3 text-center">
            <h1 className="text-h1">{t('verifyEmail.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('verifyEmail.body')}</p>
            <p className="text-sm text-muted-foreground">{t('verifyEmail.googleHint')}</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-3" noValidate>
            <p className="text-sm font-medium text-foreground">{t('verifyEmail.resend.prompt')}</p>
            <Input
              type="email"
              label={t('verifyEmail.resend.emailLabel')}
              placeholder={t('verifyEmail.resend.emailPlaceholder')}
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              disabled={status === 'sending'}
            />
            <Button type="submit" disabled={status === 'sending'} className="w-full">
              {status === 'sending'
                ? t('verifyEmail.resend.sending')
                : t('verifyEmail.resend.submit')}
            </Button>
            {outcomeKey !== null && (
              <p
                role={status === 'sent' ? 'status' : 'alert'}
                aria-live="polite"
                className={
                  status === 'sent'
                    ? 'text-sm text-muted-foreground'
                    : 'text-sm text-danger-foreground'
                }
              >
                {t(outcomeKey)}
              </p>
            )}
          </form>

          <div className="text-center">
            <Button variant="ghost" onClick={onBackToSignIn}>
              {t('verifyEmail.backToSignIn')}
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
}
